import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import type {
  Team,
  TeamInput,
  TeamsQueryParams,
  TeamsResponse
} from "./types/teams.type";

// Teams (TEAMS_FEATURE): Administration → Teams, the @ pickers, the task form and Home → For me.
// Every staff member may read; writes are admins, business owners and Suno staff (backend 403s
// everyone else). Every write invalidates `Teams`; task take/release keeps `StaffTasks` fresh.
export const teamsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<TeamsResponse, TeamsQueryParams | void>({
      query: (params) => {
        const { limit = 200, offset = 0, ...rest } = params ?? {};
        return `/teams/?${formatValidQueryParamUrlCommaSeparated({
          limit,
          offset,
          ...rest
        })}`;
      },
      // The backend pages with `CustomLimitOffsetPagination`, which answers a flat array (no count
      // envelope). Consumers read `results`, so wrap it here; an envelope is passed through as is.
      transformResponse: (response: Team[] | TeamsResponse): TeamsResponse =>
        Array.isArray(response)
          ? {
              count: response.length,
              next: null,
              previous: null,
              results: response
            }
          : response,
      providesTags: ["Teams"]
    }),
    getTeam: builder.query<Team, number>({
      query: (id) => `/teams/${id}/`,
      providesTags: ["Teams"]
    }),
    createTeam: builder.mutation<Team, TeamInput>({
      query: (body) => ({ url: "/teams/", method: "POST", body }),
      invalidatesTags: ["Teams"]
    }),
    updateTeam: builder.mutation<Team, { id: number } & Partial<TeamInput>>({
      query: ({ id, ...body }) => ({
        url: `/teams/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Teams", "StaffTasks"]
    }),
    // A team with open work is deactivated instead of deleted (backend), so nobody's unclaimed
    // task disappears; the list hides inactive teams by default.
    deleteTeam: builder.mutation<void, number>({
      query: (id) => ({ url: `/teams/${id}/`, method: "DELETE" }),
      invalidatesTags: ["Teams"]
    })
  })
});

export const {
  useGetTeamsQuery,
  useLazyGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation
} = teamsApi;
